import React, { useState } from 'react';
import { connect } from 'react-redux';
import { DateTime } from 'luxon';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';

// @mui/icons-material
import PhoneIcon from '@mui/icons-material/Phone';

// core components
import Card from '../md/components/Card/Card.jsx';
import CardHeader from '../md/components/Card/CardHeader.jsx';
import CardIcon from '../md/components/Card/CardIcon.jsx';
import CardBody from '../md/components/Card/CardBody.jsx';

import { updateReferral } from '../store/slices/referralsSlice';
import { setCaseIntendedClientId } from '../store/slices/intendedInfoSlice';
import { clearTaskAccomplishmentInfo } from '../store/slices/dashboardStatsSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import {
  createStdRequest,
  apiFetch,
  sortedDropdownItemLabels,
  isPractitionerType,
  isProprietorshipType,
} from '../utils/utils';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';

import KSelect from './KSelect';
import ReferralSelectionHierarchy from './ReferralSelectionHierarchy';
import IcfReferrerFirmModal from '../components/IcfReferrerFirmModal';
import IcfConsultancyModal from '../components/IcfConsultancyModal';
import IcfPractitionerModal from '../components/IcfPractitionerModal';
import IcfOrgContactModal from '../components/IcfOrgContactModal';
import IcfUnaffiliatedContactModal from '../components/IcfUnaffiliatedContactModal';
import NewClientContactModal from './NewClientContactModal';
import CrmButton from './CrmButton';
import CrmTextField from './CrmTextField';
import KDatePicker from './KDatePicker';
import { searchableContactDataBuilder } from './SearchableContactHierarchy';

/*
 * Call with something like
    <ReferralForm
      caseId={caseId}
      clientId={clientId}
      viewingFirmId={viewingFirmId}
      closeModalFunc={closeModalFunc}
    />
 */

const referralPageStyle = {
  cardTitle: {
    fontWeight: 400,
    fontSize: '180%',
    color: 'black',
    marginTop: '14px',
  },
  referreeNameLine: {
    fontWeight: 400,
    fontSize: '18px',
  },
};

function computeReceiverOptions() {
  const allUsers = DA.getAllFirmUsers();
  const pairs = allUsers.map((u) => {
    return { value: u.personId, label: u.name };
  });
  return pairs.sort((p1, p2) => (p1.name < p2.name ? -1 : p1.name > p2.name ? 1 : 0));
}

function ReferralForm(props) {
  const [referralDateIso, setReferralDateIso] = useState(DateTime.local().toISO());

  const [referralCategory, setReferralCategory] = useState('Company / Organization');
  const [referrerSourceType, setReferrerSourceType] = useState('Treatment Center');
  const [referringOrgId, setReferringOrgId] = useState(0);
  const [referrerId, setReferrerId] = useState('Select Contact');
  const [chosenWordOfMouthOption, setChosenWordOfMouthOption] = useState('');
  const [chosenSocialMediaOption, setChosenSocialMediaOption] = useState('');
  const [chosenMediaOption, setChosenMediaOption] = useState('');
  const [referralOptionalSpecies, setReferralOptionalSpecies] = useState('');
  const [referrerRelationship, setReferrerPersonRelationship] = useState('');

  const commTypeDropdownOptions = sortedDropdownItemLabels('Referral Communication Type');
  const [communicationType, setCommunicationType] = useState(commTypeDropdownOptions[0]);

  const [receiverId, setReceiverId] = useState(DA.getLoggedInUserPersonId());
  const [campaignId, setCampaignId] = useState(0);

  const salesStageDropdownOptions = sortedDropdownItemLabels('Sales Stage');
  const [initialSalesStage, setInitialSalesStage] = useState(salesStageDropdownOptions[0]);
  const [noteText, setNoteText] = useState('');

  const [shouldShowNewReferrerOrgModal, setShouldShowNewReferrerOrgModal] = useState(false);
  const [shouldShowNewOrgContactModal, setShouldShowNewOrgContactModal] = useState(false);
  const [shouldShowNewUnaffilContactModal, setShouldShowNewUnaffilContactModal] = useState(false);
  const [shouldShowNewClientContactModal, setShouldShowNewClientContactModal] = useState(false);
  const [shouldShowNewPractitionerModal, setShouldShowNewPractitionerModal] = useState(false);
  const [shouldShowNewProprietorshipModal, setShouldShowNewProprietorshipModal] = useState(false);

  const referree = DA.getPersonById(props.clientId);

  const justTesting = false;
  if (justTesting) {
    return <React.Fragment>This is just a test.</React.Fragment>;
  }

  let searchableContactData = searchableContactDataBuilder.build();

  const handleDateChange = (newDate) => {
    setReferralDateIso(newDate.toISO());
  };

  const handleCommunicationTypeChange = (event) => {
    const newCommType = event.target.value;
    setCommunicationType(newCommType);
  };

  const hanldeReceiverChange = (event, props) => {
    setReceiverId(event.target.value);
  };

  const handleReferralCategoryChange = (newCategory) => {
    clearHierarchyState();
    setReferralCategory(newCategory);
  };

  const handleCompanyTypeChange = (referrerType) => {
    clearHierarchyState();
    setReferrerSourceType(referrerType);
    // preselectSingleItemLists();// handled by SearchableContactHierarchy
  };

  const handleCompanyChange = (newReferrerType, newReferrerOrgId) => {
    clearHierarchyState();
    setReferrerSourceType(newReferrerType);
    setReferringOrgId(newReferrerOrgId);
    // preselectSingleItemLists();// handled by SearchableContactHierarchy
  };

  const handleContactChange = (newReferrerType, newReferrerOrgId, newContactId) => {
    if (newReferrerOrgId) {
      clearHierarchyState();
      setReferrerSourceType(newReferrerType);
      setReferringOrgId(newReferrerOrgId);
    }
    setReferrerId(newContactId);
    // preselectSingleItemLists();// handled by SearchableContactHierarchy
  };

  const clearHierarchyState = () => {
    setReferrerSourceType('');
    setReferringOrgId(0);
    setReferrerId(0);
    setChosenWordOfMouthOption('');
    setChosenSocialMediaOption('');
    setChosenMediaOption('');
    setReferralOptionalSpecies('');
    setReferrerPersonRelationship('');
  };

  const forceShowingOfNewReferrerCompanyModal = (referrerType) => {
    setReferrerSourceType(referrerType);
    if (isProprietorshipType(referrerType)) {
      setShouldShowNewProprietorshipModal(true);
    } else if (isPractitionerType(referrerType)) {
      setShouldShowNewPractitionerModal(true);
    } else {
      setShouldShowNewReferrerOrgModal(true);
    }
  };

  const forceShowingOfNewContactModal = (referrerType, referringFirmId) => {
    setReferrerSourceType(referrerType);
    setReferringOrgId(referringFirmId);
    if (referringFirmId) {
      setShouldShowNewOrgContactModal(true);
    } else {
      setShouldShowNewUnaffilContactModal(true);
    }
  };

  function handleNewReferrerOrgSuccess(visibleFirmType, newVisibleFirmId, newContactId) {
    searchableContactData = searchableContactDataBuilder.build();
    setReferrerSourceType(visibleFirmType);
    setReferringOrgId(newVisibleFirmId);
    setReferrerId(newContactId);
    // preselectSingleItemLists(); // handled by SearchableContactHierarchy
    setShouldShowNewReferrerOrgModal(false);
  }

  function handleNewOrgContactSuccess(newContactId) {
    searchableContactData = searchableContactDataBuilder.build();
    setReferrerId(newContactId);
    setShouldShowNewOrgContactModal(false);
  }

  function handleNewUnaffilContactSuccess(newContactId) {
    searchableContactData = searchableContactDataBuilder.build();
    setReferrerId(newContactId);
    setShouldShowNewUnaffilContactModal(false);
  }

  function handleNewContactModalClose() {
    setShouldShowNewOrgContactModal(false);
    setShouldShowNewUnaffilContactModal(false);
  }

  function handleNewPersonalContactModalSuccess(newContactId, relationship) {
    searchableContactData = searchableContactDataBuilder.build();
    setReferrerId(newContactId);
    setReferrerPersonRelationship(relationship);
    setShouldShowNewClientContactModal(false);
  }

  function handleNewPersonalContactModalClose() {
    setShouldShowNewClientContactModal(false);
  }

  const handleNewConsultancySuccess = (visibleFirmType, visibleFirmId, contactId) => {
    searchableContactData = searchableContactDataBuilder.build();
    setReferrerSourceType(visibleFirmType);
    setReferringOrgId(visibleFirmId);
    setReferrerId(contactId);
    setShouldShowNewProprietorshipModal(false);
  };

  const cancelProprietorshipModal = () => {
    setShouldShowNewProprietorshipModal(false);
  };

  const handleNewPractitionerSuccess = (visibleFirmType, visibleFirmId, contactId) => {
    searchableContactData = searchableContactDataBuilder.build();
    setReferrerSourceType(visibleFirmType);
    setReferringOrgId(visibleFirmId);
    setReferrerId(contactId);
    setShouldShowNewPractitionerModal(false);
  };

  const cancelPractitionerModal = () => {
    setShouldShowNewPractitionerModal(false);
  };

  const handleCampaignChange = (event) => {
    const newValue = event.target.value;
    setCampaignId(newValue);
  };

  const handleInitialSalesStageChange = (event) => {
    const newValue = event.target.value;
    setInitialSalesStage(newValue);
  };

  const handleOkClick = () => {
    validateAndSubmit();
  };

  const validateAndSubmit = () => {
    const validation = validateForm();
    if (!validation.valid) {
      alert('Something is wrong with your form: ' + validation.errorMessage);
      return;
    }
    submit();
  };

  const validateForm = () => {
    // all necessary validate was handled by the disabled check on the Submit button
    return { valid: true };
  };

  function submit() {
    createReferralInDbAndRedux();
  }

  const isFullyFilledOut = () => {
    if (referrerSourceType === 'Self') {
      return true;
    }
    if (haveChosenFullOrgType()) {
      return referringOrgId > 0 && referrerId > 0;
    }
    if (referrerSourceType === 'Private Practitioner') {
      return referringOrgId;
    }
    if (referralCategory === 'Word of Mouth') {
      return chosenWordOfMouthOption;
    }
    if (referralCategory === 'Social Media') {
      return chosenSocialMediaOption;
    }
    if (referralCategory === 'Media') {
      return chosenMediaOption;
    }
    return (
      referralCategory === 'Internet Search' ||
      referralCategory === 'Marketing Event/Campaign' ||
      referralCategory === 'Unknown'
    );
  };

  function haveChosenFullOrgType() {
    return isFirmReferrerType(referrerSourceType);
  }

  function isFirmReferrerType(referrerType) {
    return referralCategory === 'Company / Organization' && referrerType && referrerType !== 'Private Practitioner';
  }

  function createReferralInDbAndRedux() {
    const apiStruct = buildReferralApiStruct();
    const uri = `/api/referral`;
    const request = createStdRequest(uri, 'POST', apiStruct);
    const actionOnGoodResponse = (json) => {
      // ReduxLoader.addReferral(apiStruct);
      const referral = json.referral;
      ReduxLoader.addReferral(referral);
      // Gotta create note, if any.
      const trimmedNote = noteText.trim();
      const referralId = referral.referralId;
      if (trimmedNote.length > 0) {
        createNoteInDbAndRedux(referralId, trimmedNote);
      } else {
        patchCaseToIncludeReferral(props.caseId, referralId);
      }
    };
    apiFetch(null, request, actionOnGoodResponse);
  }

  function buildReferralApiStruct() {
    /*
          public int ReferralId { get; set; }
          public string ContactDate { get; set; }
          public string CommunicationType { get; set; }
          public string SourceType { get; set; }
          public string SourceSpecies { get; set; }
          public int ReferreeId { get; set; }
          public int ReferringPersonId { get; set; }
          public int ReferringOrgId { get; set; }
          public int ClientContactPersonId { get; set; }
          public string ClientContactRelationship { get; set; }
          public int ReceivingUserPersonId { get; set; }
          public int ReceivingOrgId { get; set; }
          public int CampaignId { get; set; }
          public string SalesStage { get; set; }
          public List<string> SubstanceNameStarts { get; set; } = new List<string>();
          public int CallRailCallId { get; set; }
     */

    // This was copied from NewProspectPage
    let sourceType = '';
    if (referralCategory === 'Company / Organization') {
      sourceType = referrerSourceType;
    } else if (referralCategory === 'Word of Mouth') {
      sourceType = chosenWordOfMouthOption;
      if (sourceType === 'Other') {
        sourceType = 'Other Word of Mouth';
      }
    } else if (referralCategory === 'Social Media') {
      sourceType = chosenSocialMediaOption;
      if (sourceType === 'Other') {
        sourceType = 'Other Social Media';
      }
    } else if (referralCategory === 'Media') {
      sourceType = chosenMediaOption;
      if (sourceType === 'Other') {
        sourceType = 'Other Media';
      }
    } else {
      sourceType = referralCategory;
    }

    return {
      referralId: 0,
      contactDate: referralDateIso,
      communicationType: communicationType,
      sourceType: sourceType,
      sourceSpecies: referralOptionalSpecies,
      referreeId: referree.personId,
      referringPersonId: referrerId,
      referringOrgId: referringOrgId,
      clientContactPersonId: 0,
      clientContactRelationship: '',
      receivingUserPersonId: receiverId,
      receivingOrgId: DA.getFirmId(),
      campaignId: campaignId,
      salesStage: initialSalesStage,
      substanceNameStarts: [],
      callRailCallId: 0,
    };
  }

  function createNoteInDbAndRedux(referralId, noteContent) {
    const uri = '/api/note';
    const apiStruct = createNoteApiStruct(referralId, noteContent);
    const request = createStdRequest(uri, 'POST', apiStruct);
    const actionOnGoodResponse = (json) => {
      // update Redux store
      ReduxLoader.addOrUpdateNotesOnReferrals([json]);
      patchCaseToIncludeReferral(props.caseId, referralId);
    };
    apiFetch(null, request, actionOnGoodResponse);
  }

  const createNoteApiStruct = (referralId, noteContent) => {
    const now = DateTime.local();
    const authorEmail = props.userInfo.email;
    const viewingFirmId = DA.getFirmId();
    return {
      date: now.toISO(),
      authorEmail,
      viewingFirmId,
      referralId,
      content: noteContent,
    };
  };

  function patchCaseToIncludeReferral(caseId, referralId) {
    const uri = `/api/case/${caseId}`;
    const instructions = [{ op: 'replace', path: '/referralId', value: referralId }];
    const request = createStdRequest(uri, 'PATCH', instructions);
    const actionOnGoodResponse = (json) => {
      // finally, after all the work is done, we close Dialog and repaint Case page
      props.closeModalFunc();
    };
    apiFetch(null, request, actionOnGoodResponse);
  }

  const receiverDropdownOptions = computeReceiverOptions();
  const campaigns = DA.getAllCampaigns();

  const viewingFirmId = DA.getFirmId();

  const { classes } = props;

  return (
    <React.Fragment>
      <NewClientContactModal
        show={shouldShowNewClientContactModal}
        viewingFirmId={viewingFirmId}
        clientId={props.clientId}
        relationship={referrerRelationship}
        successFunc={(newContactId, relationship) => handleNewPersonalContactModalSuccess(newContactId, relationship)}
        closeModalFunc={() => handleNewPersonalContactModalClose()}
      />

      {shouldShowNewReferrerOrgModal && (
        <IcfReferrerFirmModal
          firmType={referrerSourceType}
          onSuccess={(visibleFirmType, visibleFirmId, contactId) =>
            handleNewReferrerOrgSuccess(visibleFirmType, visibleFirmId, contactId)
          }
          onCancel={() => setShouldShowNewReferrerOrgModal(false)}
        />
      )}

      {shouldShowNewProprietorshipModal && (
        <IcfConsultancyModal
          proprietorshipType={referrerSourceType}
          onSuccess={(visibleFirmType, visibleFirmId, contactId) =>
            handleNewConsultancySuccess(visibleFirmType, visibleFirmId, contactId)
          }
          onCancel={() => cancelProprietorshipModal()}
        />
      )}

      {shouldShowNewPractitionerModal && (
        <IcfPractitionerModal
          firmType={referrerSourceType}
          onSuccess={(visibleFirmType, visibleFirmId, contactId) =>
            handleNewPractitionerSuccess(visibleFirmType, visibleFirmId, contactId)
          }
          onCancel={() => cancelPractitionerModal()}
        />
      )}

      {shouldShowNewOrgContactModal && (
        <IcfOrgContactModal
          firmId={referringOrgId}
          onSuccess={(newContactId) => handleNewOrgContactSuccess(newContactId)}
          onCancel={() => handleNewContactModalClose()}
        />
      )}

      {shouldShowNewUnaffilContactModal && (
        <IcfUnaffiliatedContactModal
          onSuccess={(newContactId) => handleNewUnaffilContactSuccess(newContactId)}
          onCancel={() => handleNewContactModalClose()}
        />
      )}

      <Grid container>
        <Grid item style={{ minWidth: '800px', maxWidth: '1200px' }}>
          <Card>
            <CardHeader color="allevaOrange" icon>
              <CardIcon color="allevaOrange">
                <PhoneIcon />
              </CardIcon>
              <div className={classes.cardTitle}>Referral</div>
            </CardHeader>
            <CardBody>
              <Grid container>
                <Grid item xs={12}>
                  <div className={classes.referreeNameLine} style={{ fontSize: '160%', padding: '10px 20px' }}>
                    Incoming Referral of <span style={{ fontWeight: 'bold' }}>{referree.preferredFullName}</span>
                  </div>
                </Grid>
                <Grid item xs={3}>
                  <div style={{ marginTop: '-4px', padding: '10px 20px' }}>
                    <KDatePicker
                      label="Referral Date"
                      value={DateTime.fromISO(referralDateIso)}
                      onChange={(newDate) => handleDateChange(newDate)}
                    />
                  </div>
                </Grid>
                <Grid item xs={3}>
                  <div style={{ padding: '10px 20px' }}>
                    <KSelect
                      id="communication-type"
                      name="communicationType"
                      label="Communication Type"
                      fullWidth={true}
                      disabledOption="Choose Type"
                      liveOptions={commTypeDropdownOptions}
                      value={communicationType}
                      onChange={(event) => handleCommunicationTypeChange(event)}
                    />
                  </div>
                </Grid>
                <Grid item xs={3}>
                  <div style={{ padding: '10px 20px' }}>
                    <KSelect
                      id="receiver-selector"
                      label="Received By"
                      fullWidth={true}
                      disabledOption="Choose Receiver"
                      liveOptions={receiverDropdownOptions.map((o) => o.label)}
                      liveValues={receiverDropdownOptions.map((o) => o.value)}
                      value={receiverId}
                      onChange={(event) => hanldeReceiverChange(event, props)}
                    />
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <div style={{ padding: '10px 20px', marginTop: '-20px' }}>
                    <ReferralSelectionHierarchy
                      searchableContactData={searchableContactData}
                      chosenReferralCategory={referralCategory}
                      companyType={referrerSourceType}
                      companyId={referringOrgId}
                      contactId={referrerId}
                      chosenWordOfMouthOption={chosenWordOfMouthOption}
                      chosenSocialMediaOption={chosenSocialMediaOption}
                      chosenMediaOption={chosenMediaOption}
                      referralOptionalSpecies={referralOptionalSpecies}
                      onReferralCategoryChange={(referralCategory) => handleReferralCategoryChange(referralCategory)}
                      onCompanyTypeChange={(referrerType) => handleCompanyTypeChange(referrerType)}
                      onCompanyChange={(referrerType, referrerOrgId) =>
                        handleCompanyChange(referrerType, referrerOrgId)
                      }
                      onContactChange={(referrerType, referrerOrgId, personId) =>
                        handleContactChange(referrerType, referrerOrgId, personId)
                      }
                      onWordOfMouthOptionChange={(newChoice) => setChosenWordOfMouthOption(newChoice)}
                      onSocialMediaOptionChange={(newChoice) => setChosenSocialMediaOption(newChoice)}
                      onMediaOptionChange={(newChoice) => setChosenMediaOption(newChoice)}
                      onOptionalSpeciesChange={(newValue) => setReferralOptionalSpecies(newValue)}
                      onCreateNewCompany={(referrerType) => forceShowingOfNewReferrerCompanyModal(referrerType)}
                      onCreateNewContact={(referrerType, firmId) => forceShowingOfNewContactModal(referrerType, firmId)}
                    />
                  </div>
                </Grid>
                <Grid item xs={9}>
                  <div style={{ padding: '10px 20px' }}>
                    <KSelect
                      id="campaign"
                      label="Campaign"
                      fullWidth={true}
                      disabledOption='Select Campaign (or "None")'
                      liveOptions={['None'].concat(campaigns.map((c) => c.name))}
                      liveValues={[0].concat(campaigns.map((c) => c.campaignId))}
                      value={campaignId}
                      onChange={(event) => handleCampaignChange(event)}
                    />
                  </div>
                </Grid>
                <Grid item xs={3}>
                  <div style={{ padding: '10px 20px' }}>
                    <KSelect
                      id="sales-stage"
                      label="Initial Sales Stage"
                      fullWidth={true}
                      disabledOption="Choose Stage"
                      liveOptions={salesStageDropdownOptions}
                      value={initialSalesStage}
                      onChange={(event) => handleInitialSalesStageChange(event)}
                    />
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <div style={{ padding: '10px 20px' }}>
                    <CrmTextField
                      multiline
                      fullWidth
                      label="Note"
                      value={noteText}
                      onChange={(event) => setNoteText(event.target.value)}
                    />
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <div style={{ float: 'right', marginTop: '12px', padding: '10px 20px' }}>
                    <div style={{ display: 'inline-block' }}>
                      <CrmButton outlined size="small" onClick={props.closeModalFunc}>
                        Cancel
                      </CrmButton>
                    </div>
                    <div style={{ display: 'inline-block' }}>
                      <CrmButton filled size="small" disabled={!isFullyFilledOut()} onClick={() => handleOkClick()}>
                        Submit
                      </CrmButton>
                    </div>
                  </div>
                </Grid>
                {false && (
                  <Grid item xs={12} style={{ background: '#ffeeee' }}>
                    <div style={{ padding: '10px 20px' }}>Substances of Concern</div>
                  </Grid>
                )}
              </Grid>
            </CardBody>
          </Card>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

const mapStateToProps = (state) => ({
  referralId: state.intendedInfo.intendedReferralId,
  firmInfo: state.firmInfo,
  userInfo: state.userInfo,
});

const actionProducers = {
  updateReferral,
  setCaseIntendedClientId,
  clearTaskAccomplishmentInfo,
  setApiResponseInfo,
};

export default connect(mapStateToProps, actionProducers)(withStyles(referralPageStyle)(ReferralForm));
